import axios from 'axios';
import { isTokenValid, clearAdminAuth } from '../utils/auth';
import {
  Vehicle,
  VehicleList,
  PaginatedResponse,
  VehicleSearchParams,
  SearchFilters,
  Statistics,
  SyncStatus
} from '../types/api';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token && isTokenValid(token)) {
      if (!config.headers) {
        config.headers = {} as any;
      }
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors here
    if (error.response?.status === 401) {
      clearAdminAuth();
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const vehicleApi = {
  // Search vehicles with filters and pagination
  searchVehicles: async (params: VehicleSearchParams): Promise<PaginatedResponse<VehicleList>> => {
    const response = await api.get('/vehicles/', { params });
    return response.data;
  },

  // Get a specific vehicle by ID
  getVehicle: async (id: number): Promise<Vehicle> => {
    const response = await api.get(`/vehicles/${id}`);
    return response.data;
  },

  // Get a vehicle by VIN
  getVehicleByVin: async (vin: string): Promise<Vehicle> => {
    const response = await api.get(`/vehicles/vin/${vin}`);
    return response.data;
  },

  // Get featured vehicles
  getFeaturedVehicles: async (limit: number = 10): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/featured', { params: { limit } });
    return response.data;
  },

  // Get vehicles ending soon
  getEndingSoon: async (limit: number = 10): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/ending-soon', { params: { limit } });
    return response.data;
  },

  // Get recent arrivals
  getRecentArrivals: async (limit: number = 10): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/recent-arrivals', { params: { limit } });
    return response.data;
  },

  // Get random luxury/supercar vehicles (fast, DB-side random)
  getRandomLuxury: async (limit: number = 4): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/random-luxury', { params: { limit } });
    return response.data;
  },

  // Get random buy-now vehicles (fast, DB-side random)
  getRandomBuyNow: async (limit: number = 4): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles/random-buy-now', { params: { limit } });
    return response.data;
  },

  // Get search filters
  getSearchFilters: async (): Promise<SearchFilters> => {
    const response = await api.get('/vehicles/filters');
    return response.data;
  },

  // Get statistics
  getStatistics: async (): Promise<Statistics> => {
    const response = await api.get('/vehicles/statistics');
    return response.data;
  },

  // Get sync status
  getSyncStatus: async (): Promise<SyncStatus> => {
    const response = await api.get('/vehicles/sync/status');
    return response.data;
  },

  // Trigger manual sync
  triggerSync: async (maxPages?: number): Promise<any> => {
    const params = maxPages ? { max_pages: maxPages } : {};
    const response = await api.post('/vehicles/sync', {}, { params });
    return response.data;
  },

  // Archive sold vehicles
  archiveSoldVehicles: async (daysThreshold: number = 7): Promise<any> => {
    const response = await api.post('/vehicles/archive-sold', {}, {
      params: { days_threshold: daysThreshold }
    });
    return response.data;
  },
};

export const featuredInventoryApi = {
  // Get all featured inventory vehicles
  getFeaturedInventory: async (vehicleType?: string): Promise<any[]> => {
    const params: any = {};
    if (vehicleType) {
      params.vehicle_type = vehicleType;
    }
    const response = await api.get('/featured-inventory/', { params });
    return response.data;
  },

  // Get featured inventory vehicles that are available and featured (only 'available' type, not dealer)
  getHomepageCarousel: async (): Promise<any[]> => {
    const response = await api.get('/featured-inventory/', {
      params: { vehicle_type: 'available' }
    });
    // Filter for available and featured vehicles
    return response.data.filter((v: any) => v.is_available && v.is_featured && !v.is_sold);
  },

  // Get "Налични автомобили" (available in Bulgaria)
  getAvailableVehicles: async (): Promise<any[]> => {
    const response = await api.get('/featured-inventory/', { 
      params: { vehicle_type: 'available' } 
    });
    return response.data.filter((v: any) => v.is_available && !v.is_sold);
  },

  // Get "Дилърски коли от Канада" (Canada dealer vehicles)
  getCanadaDealerVehicles: async (): Promise<any[]> => {
    const response = await api.get('/featured-inventory/', { 
      params: { vehicle_type: 'canada_dealer' } 
    });
    return response.data.filter((v: any) => v.is_available && !v.is_sold);
  },
};

export default api;